import { ComponentProps } from 'react';
import Component from '@cx-modules/odds-grid-vertical';
import OddsGridVerticalCss from '@cx-modules/odds-grid-vertical/style.css?inline';
import { widgetBootstrap } from '../../services/cx-module-helper';
import disclaimerService from '../../services/disclaimer';
import {
  Keys,
  SanitisedContractProps as SanitisedProps
} from '../../utils/contractProps/contractProps.js';
import css from '../../utils/css';
import { resolveUsGeo } from '../../utils/geolocation/geolocation.js';
import disclaimer from './disclaimer';
import themes from './themes';
import themeContract from './themes/contract';
import { meta } from './meta.js';

export const attributes = meta.attributes;
export const widgetName = meta.name;

const {
  VITE_CLICKOUT_SERVICE_URL: clickoutServiceUrl,
  VITE_CX_API_HOST: ocApiHost
} = import.meta.env;

type ContractProps = ComponentProps<typeof Component>;

type Repub = ContractProps['repub'];
type CountryCode = ContractProps['countryCode'];
type Subdivision = ContractProps['subdivision'];
type CategoryGroupId = ContractProps['categoryGroupId'];
type BookmakersConfig = ContractProps['bookmakersConfig'];
type OddsFormat = ContractProps['oddsFormat'];

type OmittedProps = Keys<
  ContractProps,
  // supplied by widgetBootstrap
  | 'dehydratedState'
  | 'i18n'
  // required upstream, deliberately not passed
  | 'deviceType'
  | 'marketIndex'
  | 'oddsShown'
  | 'resourcesUrl'
>;

type SanitisedContractProps = SanitisedProps<
  ContractProps,
  OmittedProps,
  'countryCode' | 'subdivision'
>;

const resolveUsGeolocation = (geolocation: string) =>
  resolveUsGeo<CountryCode, Subdivision>(geolocation);

type AnalyticsDataLayer = ContractProps['analyticsDataLayer'];
const getAnalyticsDataLayer = (
  dataLayerName: string
): AnalyticsDataLayer | undefined =>
  (window as unknown as Record<string, AnalyticsDataLayer | undefined>)[
    dataLayerName
  ];

type ContractParams = {
  affIds?: Record<string, string>;
  aiContextual?: unknown;
  analytics: { dataLayerName: string };
  apiKey: string;
  betIdsToShow?: number[];
  bookies: BookmakersConfig;
  campaignName?: string;
  categoryGroupId: CategoryGroupId;
  clickoutRedirectTime?: string;
  clickoutTemplate?: string;
  geolocation: string;
  inRunning: boolean;
  marketId: number;
  marketName: string;
  minUniqueBookies?: number;
  noDataAvailableFallbackText?: string;
  oddsFormat: OddsFormat;
  recommendedMarketId?: number;
  repub: Repub;
  subeventName: string;
  widgetStyle?: string;
  showSubeventNameAndDatetime?: boolean;
};

type ContractAttr = HTMLElement & {
  slotId?: string;
};

const contract = async (
  {
    affIds,
    aiContextual,
    analytics,
    apiKey,
    betIdsToShow,
    bookies,
    campaignName,
    categoryGroupId,
    clickoutRedirectTime,
    clickoutTemplate,
    geolocation,
    inRunning,
    marketId,
    marketName,
    minUniqueBookies,
    noDataAvailableFallbackText,
    oddsFormat,
    recommendedMarketId,
    repub,
    subeventName,
    widgetStyle: theme,
    showSubeventNameAndDatetime: showSubeventData
  }: ContractParams,
  attr: ContractAttr
): Promise<SanitisedContractProps | undefined> => {
  const isUsRepub = repub === 'US';

  const cxProps: SanitisedContractProps = {
    analyticsDataLayer: getAnalyticsDataLayer(analytics.dataLayerName),
    analyticsImpressionEventData: {
      variation: widgetName,
      widgetSlot: attr.slotId,
      campaignName,
      ...(recommendedMarketId ? { recommendedMarketId } : {})
    },
    affIds,
    apiKey,
    betIdsToShow,
    bookmakersConfig: bookies,
    categoryGroupId,
    clickoutRedirectTime,
    clickoutServiceUrl,
    clickoutTemplate,
    dataApiEndpoint: ocApiHost,
    inRunning,
    marketId,
    marketName,
    minUniqueBookies,
    noDataAvailableFallbackText,
    oddsFormat,
    overrideBookies: Object.keys(bookies),
    repub,
    subeventName,
    theme,
    showSubeventData,
    ...(isUsRepub && resolveUsGeolocation(geolocation))
  };

  const shouldInjectCxModule = (aiContextual && marketId) || !aiContextual;

  if (shouldInjectCxModule) {
    return cxProps;
  } else {
    console.log(`Skipping cxm flow - no marketId`);
  }
};

type QueryKeyParams = {
  apiKey: string;
  marketId?: number;
  repub?: Repub;
  geolocation: string;
  bookies: BookmakersConfig;
};

const customCss = css`
  :host {
    --disclaimer-background: var(--odds-grid-bg);
    --disclaimer-border-radius: 0;
    --disclaimer-padding: 0 10px;
    --disclaimer-font-size: 12px;
    --disclaimer-border-color: var(--odds-grid-border);
    --disclaimer-divider-color: var(--odds-grid-border);
    --disclaimer-font-family: var(--odds-grid-vertical-font-family);
  }

  slot[name='disclaimer'] footer {
    margin-top: -4px;
    border: 1px solid var(--disclaimer-border-color);
    border-top: none;
    padding-top: 0;
  }

  slot[name='disclaimer'] .icons {
    border-top: 1px solid var(--disclaimer-divider-color);
    padding: 5px 0;
    margin-top: 5px;
    flex-direction: row-reverse;
    font-size: 8px;
  }

  slot[name='disclaimer'] .icons img {
    height: 12px;
  }

  slot[name='disclaimer'] .poweredby span {
    height: 11px;
  }

  slot[name='disclaimer'] .poweredby img {
    filter: grayscale(100%) brightness(10%);
  }
`;

widgetBootstrap({
  attributes,
  Component,
  customCss,
  contract,
  queryKey({ apiKey, marketId, repub, geolocation, bookies }: QueryKeyParams) {
    const overrideBookies = Object.keys(bookies);
    const isUsRepub = repub === 'US';
    const usGeo = isUsRepub ? resolveUsGeolocation(geolocation) : undefined;

    return [
      'odds-grid-vertical',
      apiKey,
      repub,
      usGeo?.countryCode,
      usGeo?.subdivision,
      marketId,
      overrideBookies?.sort().join(',') ?? null
    ];
  },
  disclaimerService,
  disclaimer,
  name: widgetName,
  style: OddsGridVerticalCss,
  themes,
  themeContract,
  version: '1.1.0' // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
});
